import { formatCurrency } from '../receptionistData';
import {
  combineMethodLabels,
  paymentMethodLabel,
  serializePaymentSplits,
} from '../../../utils/splitPaymentUtils';
import {
  formatDisplayDate,
  formatDurationLabel,
  addDurationToDate,
  isValidIsoDate,
  cleanSalePackageName,
  splitSalePackageAndDuration,
  SALE_DURATION_OPTIONS,
  GUEST_PASS_DURATION_OPTIONS,
  SPA_OXYBAR_DURATION_OPTIONS,
} from './saleDurationUtils';
import {
  getSaleRowPeriod,
  saleRowPackageName,
  saleRowTrainerName,
  isTrainerSaleSource,
  stripSaleTypePrefix,
} from './salePeriodUtils';
import { saleRowLeft, saleRowPaid, saleRowTotal } from './salePaymentUtils';

const ALL_DURATION_OPTIONS = [
  ...SALE_DURATION_OPTIONS,
  ...GUEST_PASS_DURATION_OPTIONS,
  ...SPA_OXYBAR_DURATION_OPTIONS,
];

function receiptDate(value) {
  return isValidIsoDate(value) ? String(value).slice(0, 10) : '';
}

/** Resolve a human duration label from key, stored label, item text, or period note. */
function resolveReceiptDurationLabel(duration, itemText = '', periodLabel = '') {
  const fromPeriod = String(periodLabel || '').trim();
  if (fromPeriod && fromPeriod !== '—') return fromPeriod;

  const raw = String(duration || '').trim();
  if (raw && raw !== '—') {
    const byValue = ALL_DURATION_OPTIONS.find((o) => String(o.value) === raw);
    if (byValue) return byValue.label;
    const byLabel = ALL_DURATION_OPTIONS.find(
      (o) => String(o.label).toLowerCase() === raw.toLowerCase(),
    );
    if (byLabel) return byLabel.label;
    const formatted = formatDurationLabel(raw, ALL_DURATION_OPTIONS);
    if (formatted && formatted !== '—') return formatted;
    return raw;
  }

  const fromItem = splitSalePackageAndDuration(itemText).durationLabel;
  if (fromItem) return fromItem;

  return '—';
}

function resolveReceiptDurationKey(rawDuration, durationLabel) {
  const raw = String(rawDuration || '').trim();
  if (/^\d+[dm]$/i.test(raw)) return raw.toLowerCase();
  const label = String(durationLabel || raw || '').trim().toLowerCase();
  if (!label) return '';
  const found = ALL_DURATION_OPTIONS.find(
    (o) => String(o.label || '').trim().toLowerCase() === label,
  );
  return found?.value || '';
}

function resolveCustomerPhone({ phone, mobile, customerPhone, paymentNote } = {}) {
  const direct = String(phone || mobile || customerPhone || '').trim();
  if (direct && direct !== '—') return direct;
  const note = String(paymentNote || '');
  const match = note.match(/Phone:\s*([^·|]+)/i);
  const fromNote = match ? String(match[1] || '').trim() : '';
  return fromNote && fromNote !== '—' ? fromNote : '';
}

export function buildSaleReceiptData({
  saleTitle,
  saleLabel,
  customer,
  gymId,
  phone,
  mobile,
  customerPhone,
  item,
  itemFieldLabel = 'Item',
  duration,
  startDate,
  endDate,
  transactionDate,
  totalAmount,
  paymentAmount,
  totalLeft,
  paymentSplits,
  paymentNote,
  status,
  invoiceId,
  paymentId,
  complimentary = false,
  durationLabel: durationLabelHint = '',
}) {
  const split = splitSalePackageAndDuration(item);
  const cleanItem = stripSaleTypePrefix(cleanSalePackageName(
    item,
    split.packageName || String(saleLabel || saleTitle || '').trim() || '—',
  )) || cleanSalePackageName(
    item,
    split.packageName || String(saleLabel || saleTitle || '').trim() || '—',
  );
  const durationLabel = resolveReceiptDurationLabel(
    duration || durationLabelHint,
    item,
    durationLabelHint,
  );
  const periodStart = isValidIsoDate(startDate) ? String(startDate).slice(0, 10) : '';
  const periodEndStored = isValidIsoDate(endDate) ? String(endDate).slice(0, 10) : '';
  const durationKey = resolveReceiptDurationKey(duration, durationLabel);
  const periodEnd = periodEndStored
    || (periodStart && durationKey ? addDurationToDate(periodStart, durationKey) : '');
  const paidAt = isValidIsoDate(transactionDate)
    ? String(transactionDate).slice(0, 10)
    : (periodStart || '');
  const serializedSplits = complimentary ? [] : serializePaymentSplits(paymentSplits);
  const splitLines = serializedSplits.map((splitRow) => ({
    label: paymentMethodLabel(splitRow.method),
    value: formatCurrency(splitRow.amount),
  }));

  const resolvedPhone = resolveCustomerPhone({ phone, mobile, customerPhone, paymentNote });

  return {
    invoiceId,
    paymentId: complimentary ? '—' : paymentId,
    saleTitle,
    saleLabel,
    customer,
    gymId: gymId || '—',
    phone: resolvedPhone || '—',
    mobile: resolvedPhone || '—',
    customerPhone: resolvedPhone || '—',
    item: cleanItem || '—',
    package: cleanItem || '—',
    itemFieldLabel: itemFieldLabel || 'Item',
    durationLabel: durationLabel || '—',
    startDate: formatDisplayDate(periodStart),
    endDate: formatDisplayDate(periodEnd),
    date: formatDisplayDate(paidAt),
    totalAmount: complimentary ? 0 : (Number(totalAmount) || 0),
    paymentAmount: complimentary ? 0 : (Number(paymentAmount) || 0),
    totalLeft: complimentary ? 0 : (Number(totalLeft) || 0),
    totalAmountDisplay: complimentary ? 'Complimentary' : formatCurrency(totalAmount),
    paymentAmountDisplay: complimentary ? 'Complimentary' : formatCurrency(paymentAmount),
    totalLeftDisplay: formatCurrency(complimentary ? 0 : totalLeft),
    paymentSplits: serializedSplits,
    paymentType: complimentary ? 'Complimentary' : combineMethodLabels(paymentSplits),
    splitLines,
    paymentNote: paymentNote || '',
    status: complimentary ? 'complimentary' : (status || 'completed'),
    complimentary: Boolean(complimentary),
  };
}

/** Build receipt preview/export data from an existing payment row. */
export function buildReceiptFromPaymentRow(row, saleMeta = {}, options = {}) {
  const period = getSaleRowPeriod(row);
  const totalAmount = saleRowTotal(row);
  const cumulativePaid = saleRowPaid(row);
  const installmentAmount = Number(options.installmentAmount) || 0;
  const installmentSplits = options.installmentSplits || [];

  let totalLeft = saleRowLeft(row);
  if (options.pendingAfterPay != null) {
    totalLeft = Math.max(0, Number(options.pendingAfterPay));
  } else if (Number(row.pendingAmount) >= 0) {
    totalLeft = Math.max(0, Number(row.pendingAmount));
  }

  const paymentAmountForReceipt = installmentAmount > 0 ? installmentAmount : cumulativePaid;
  const splitsForReceipt = installmentAmount > 0 ? installmentSplits : row.paymentSplits;
  const trainerSale = isTrainerSaleSource(row.source) || Boolean(saleMeta.itemIsTrainer);
  const cleanItem = trainerSale
    ? saleRowTrainerName(row, row.package || '—')
    : saleRowPackageName(row, row.package || '—');
  const durationLabel = resolveReceiptDurationLabel(
    row.duration || row.durationLabel,
    row.package || row.item,
    period.durationLabel,
  );

  const receipt = buildSaleReceiptData({
    saleTitle: saleMeta.title || row.source || 'Sale',
    saleLabel: saleMeta.label || row.source || 'Sale',
    customer: row.member,
    gymId: row.gymId,
    phone: row.phone || row.mobile || saleMeta.phone || '',
    item: cleanItem,
    itemFieldLabel: trainerSale ? 'Trainer' : (saleMeta.itemFieldLabel || 'Item'),
    duration: row.duration || period.durationLabel || durationLabel,
    durationLabel,
    startDate: period.startDate,
    endDate: period.endDate,
    transactionDate: options.transactionDate || row.date,
    totalAmount,
    paymentAmount: paymentAmountForReceipt,
    totalLeft,
    paymentSplits: splitsForReceipt,
    paymentNote: row.paymentNote,
    status: row.status,
    invoiceId: row.invoiceId,
    paymentId: row.id,
    complimentary: Boolean(row.complimentary)
      || String(row.status || '').toLowerCase() === 'complimentary',
  });

  // Always prefer cleaned item + resolved duration (history / thermal reprint)
  receipt.item = cleanItem || receipt.item;
  receipt.package = receipt.item;
  receipt.itemFieldLabel = trainerSale ? 'Trainer' : (receipt.itemFieldLabel || 'Item');
  receipt.durationLabel = durationLabel || receipt.durationLabel;

  receipt.pendingAmount = totalLeft;
  receipt.cumulativePaid = cumulativePaid;
  receipt.cumulativePaidDisplay = formatCurrency(cumulativePaid);
  receipt.packagePrice = Number(row.packagePrice || row.package_price) || 0;
  receipt.packagePriceDisplay = formatCurrency(receipt.packagePrice);
  receipt.registrationFee = Number(row.registrationFee || row.registration_fee) || 0;
  receipt.registrationFeeDisplay = formatCurrency(receipt.registrationFee);
  receipt.saleDate = formatDisplayDate(
    receiptDate(options.saleDate || row.date || row.paidAt || row.startDate),
  );
  receipt.saleDateRaw = receiptDate(options.saleDate || row.date || row.paidAt || row.startDate);
  receipt.balanceBefore = Number(options.balanceBefore);
  if (Number.isFinite(receipt.balanceBefore) && receipt.balanceBefore >= 0) {
    receipt.balanceBeforeDisplay = formatCurrency(receipt.balanceBefore);
  }
  receipt.isRemainingClear = Boolean(options.isRemainingClear || installmentAmount > 0);

  if (installmentAmount > 0) {
    receipt.installmentAmount = installmentAmount;
    receipt.installmentSplits = installmentSplits;
    receipt.installmentDisplay = formatCurrency(installmentAmount);
    receipt.installmentMethod = combineMethodLabels(installmentSplits);
    receipt.totalLeft = totalLeft;
    receipt.totalLeftDisplay = formatCurrency(totalLeft);
    receipt.saleTitle = options.saleTitle || saleMeta.title || 'Remaining Clear';
    receipt.saleLabel = options.saleLabel || saleMeta.label || 'Remaining Clear';
  }

  return receipt;
}

/**
 * Invoice for a remaining-balance payment:
 * package, package/total price, original sale date, payment date,
 * remaining before, amount paid now, remaining after.
 */
export function buildRemainingClearReceipt(row, {
  installmentAmount,
  installmentSplits,
  transactionDate,
  balanceBefore,
  pendingAfterPay,
  saleMeta = {},
} = {}) {
  const before = Number.isFinite(Number(balanceBefore))
    ? Math.max(0, Number(balanceBefore))
    : saleRowLeft(row) + (Number(installmentAmount) || 0);
  const paidNow = Number(installmentAmount) || 0;
  const after = Number.isFinite(Number(pendingAfterPay))
    ? Math.max(0, Number(pendingAfterPay))
    : Math.max(0, before - paidNow);

  return buildReceiptFromPaymentRow(row, {
    title: 'Remaining Clear',
    label: 'Remaining Clear',
    itemFieldLabel: saleMeta.itemFieldLabel || 'Package',
    ...saleMeta,
  }, {
    installmentAmount: paidNow,
    installmentSplits,
    transactionDate,
    pendingAfterPay: after,
    balanceBefore: before,
    saleDate: row.date || row.startDate || row.paidAt,
    isRemainingClear: true,
    saleTitle: 'Remaining Clear',
    saleLabel: 'Remaining Clear',
  });
}
